import { useEffect, useRef, useState } from "react"
import { HighFive, Player } from "../types"
import { ChatMessage } from "./ChatMessage"
import { TextField, Button, ButtonGroup, Paper } from "@mui/material"
import { Send as SendIcon, Videocam as VideocamIcon, TextFields as TextFieldsIcon } from "@mui/icons-material"
import "./ChatPage.css"
import moment from "moment"
import { Dialog } from "./Dialog"
import { CustomVideoRecorder } from "./CustomVideoRecorder"
import _ from "lodash"
import { highFiveValue } from "../utils/constants"

export interface HighFiveContent {
  message?: string
  videoBlob?: Blob
}

export interface ChatPageProps {
  messages: HighFive[]
  player: Player
  onSend: (messageOrVideo: HighFiveContent) => Promise<void>
}

export const ChatPage: React.FC<ChatPageProps> = ({ messages, player, onSend }) => {
  const [typedMessage, setTypedMessage] = useState("")
  const [videoBlob, setVideo] = useState<undefined | Blob>(undefined)
  const [isInVideoSendMode, setIsInVideoSendMode] = useState(false)

  const endOfChatDiv = useRef<null | HTMLDivElement>(null)
  useEffect(() => {
    if (endOfChatDiv) {
      setTimeout(() => {
        // @ts-ignore - TS bug doesn't accept "instant"
        endOfChatDiv.current?.scrollIntoView({behavior: "instant"})
      }, 100)
    }
  }, [endOfChatDiv])

  const playerHasSentMessageToday =
    !!messages.find(
      m =>
        m.senderId === player.id
        && moment().isSame(m.date, 'day')
    )

  const lastReceivedMessage = _.last(messages)
  
  const playerHasSentVideo = !!messages.find(m => m.senderId === player.id)
  const playerHasRecentlySentVideo =
    !!messages.find(m =>
      moment().diff(m.date, 'days') < 5
      && m.senderId === player.id
    )

  const [mode, setMode] = useState<"text" | "video">(
    (
      lastReceivedMessage?.videoFileName
      || !playerHasRecentlySentVideo
    ) ?
      "video"
    :
      "text"
  )

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setTypedMessage(e.target.value)
  }

  const handleSendMessage = () => {
    const messageToSend = typedMessage !== '' ? typedMessage : `High five! 👋`
    onSend({ message: messageToSend, videoBlob })
  }
  
  const handleSwitchMode = () => {
    const newMode = mode === "video" ? "text" : "video"
    if (newMode === 'video') {
      setIsInVideoSendMode(true)
    }
    setMode(newMode)
  }

  return (
    <div>
      {messages.map((chat, i) => (
        <div
          key={i}
          className={
            chat.senderId === player.id
              ? "ChatPage--sentMessage"
              : "ChatPage--receivedMessage"
          }
        >
          <ChatMessage highFive={chat} player={player} />
        </div>
      ))}
      {
        !playerHasSentMessageToday &&
          <div className="ChatPage--highFiveText">
           👋 Send a high five and your partner gets {highFiveValue} points!
          </div>
      }
      <div>
        {
          mode === "video" ? (
            <ButtonGroup>
              <Button
                onClick={() => setIsInVideoSendMode(true)}
                startIcon={<VideocamIcon />}
                size="large"
                variant="contained"
              >
                {
                  lastReceivedMessage?.videoFileName ?
                    "Respond with a smile!"
                  : !playerHasSentVideo ?
                    "Start with a \"hi\"!"
                  : !playerHasRecentlySentVideo ?
                    "Send a quick fist pump!"
                  :
                    "Send video"
                }
              </Button>
              <Button
                onClick={handleSwitchMode}
                startIcon={<TextFieldsIcon />}
                size="large"
                variant="outlined"
              />
            </ButtonGroup>
          ) : (
            <div className="ChatPage--sendSection">
              <TextField
                value={typedMessage}
                onChange={handleInputChange}
              />
              <ButtonGroup>
                <Button onClick={handleSendMessage} variant="contained">
                  <SendIcon />
                </Button>
                <Button onClick={handleSwitchMode} variant="outlined">
                  <VideocamIcon />
                </Button>
              </ButtonGroup>
            </div>
          )
        }
      </div>
      {
        isInVideoSendMode &&
          <Dialog
            submitText='Send!'
            isSubmitDisabled={_.isNil(videoBlob)}
            onSubmit={handleSendMessage}
            onClose={() => setIsInVideoSendMode(false)}
          >
            <CustomVideoRecorder
              onStartRecording={() => setVideo(undefined)}
              onRecordingComplete={(videoBlob) => setVideo(videoBlob)}
            />
          </Dialog>
      }
      <div ref={endOfChatDiv}></div>
    </div>
  )
}
